import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Users, Video, TrendingUp, DollarSign, Activity,
  ArrowUpRight, ArrowDownRight, Eye, Play, Clock
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/admin';

// Stats card component
function StatCard({ title, value, change, changeType, icon: Icon, color, loading }) {
  const colors = {
    indigo: 'from-indigo-500/20 to-indigo-600/10 border-indigo-500/20',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/20',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/20',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/20',
  };
  
  const iconColors = {
    indigo: 'bg-indigo-500/20 text-indigo-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/20 text-amber-400',
    purple: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br ${colors[color]} rounded-2xl p-6 border`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-400 text-sm font-medium mb-1">{title}</p>
          {loading ? (
            <div className="h-9 w-24 bg-zinc-800 rounded animate-pulse" />
          ) : (
            <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </h3>
          )}
          {change !== undefined && !loading && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${changeType === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
              {changeType === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{change}% from last period</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

// Live stream card
function LiveStreamCard({ stream }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-zinc-800/30 rounded-xl hover:bg-zinc-800/50 transition-all">
      <div className="relative">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          {stream.profile_picture ? (
            <img 
              src={`https://dev.naofan.app/avatars/${stream.profile_picture}`} 
              alt={stream.broadcaster_name}
              className="w-14 h-14 rounded-xl object-cover"
            />
          ) : (
            <span className="text-white font-bold text-lg">
              {stream.broadcaster_name?.charAt(0) || 'N'}
            </span>
          )}
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-zinc-900 animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium truncate">{stream.title}</h4>
        <p className="text-zinc-500 text-sm truncate">{stream.broadcaster_name || 'Anonymous'}</p>
      </div>
      <div className="text-right">
        <div className="flex items-center gap-1 text-zinc-400 text-sm">
          <Eye className="w-4 h-4" />
          {stream.viewer_count}
        </div>
        <div className="flex items-center gap-1 text-amber-400 text-sm mt-1">
          <DollarSign className="w-4 h-4" />
          {stream.total_earnings?.toFixed(2) || '0.00'}
        </div>
      </div>
    </div>
  );
}

// Top broadcaster row
function BroadcasterRow({ broadcaster, rank }) {
  return (
    <div className="flex items-center gap-4 py-3">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        rank === 1 ? 'bg-amber-500 text-white' :
        rank === 2 ? 'bg-zinc-400 text-white' :
        rank === 3 ? 'bg-amber-700 text-white' :
        'bg-zinc-700 text-zinc-400'
      }`}>
        {rank}
      </span>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
        {broadcaster.profile_picture ? (
          <img 
            src={`https://dev.naofan.app/avatars/${broadcaster.profile_picture}`}
            alt={broadcaster.name}
            className="w-10 h-10 object-cover"
          />
        ) : (
          <span className="text-white font-bold">{broadcaster.name?.charAt(0) || 'N'}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{broadcaster.name || 'Anonymous'}</p>
        <p className="text-zinc-500 text-xs">{broadcaster.total_streams} streams</p>
      </div>
      <div className="text-right">
        <p className="text-amber-400 font-bold">{broadcaster.total_earnings?.toFixed(2)} tokens</p>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { token } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [userChartData, setUserChartData] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [topBroadcasters, setTopBroadcasters] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('30');

  useEffect(() => {
    fetchDashboardData();
    // Refresh live streams every 30 seconds
    const interval = setInterval(fetchLiveStreams, 30000);
    return () => clearInterval(interval);
  }, [chartPeriod]);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, usersRes, revenueRes, liveRes, broadcastersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/dashboard/charts/users?period=${chartPeriod}`, { headers }),
        axios.get(`${API}/dashboard/charts/revenue?period=${chartPeriod}`, { headers }),
        axios.get(`${API}/dashboard/live-streams`, { headers }),
        axios.get(`${API}/dashboard/top-broadcasters`, { headers }),
      ]);

      setStats(statsRes.data);
      setUserChartData(usersRes.data.data || []);
      setRevenueChartData(revenueRes.data.data || []);
      setLiveStreams(liveRes.data.streams || []);
      setTopBroadcasters(broadcastersRes.data.broadcasters || []);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveStreams = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/live-streams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveStreams(res.data.streams || []);
    } catch (err) {
      console.error('Failed to fetch live streams:', err);
    }
  };

  // Chart colors
  const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef'];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <Toaster position="top-center" theme="dark" />

      {/* Overview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.overview?.totalUsers}
          change={stats?.thisWeek?.newUsers > 0 ? '+' + stats.thisWeek.newUsers : 0}
          changeType="up"
          icon={Users}
          color="indigo"
          loading={loading}
        />
        <StatCard
          title="Total Streams"
          value={stats?.overview?.totalStreams}
          change={stats?.today?.newStreams}
          changeType="up"
          icon={Video}
          color="purple"
          loading={loading}
        />
        <StatCard
          title="Active Streams"
          value={stats?.overview?.activeStreams}
          icon={Activity}
          color="emerald"
          loading={loading}
        />
        <StatCard
          title="Total Revenue"
          value={stats?.overview?.totalRevenue?.toFixed(2) + ' tokens'}
          change={((stats?.thisMonth?.revenue / stats?.overview?.totalRevenue) * 100).toFixed(1)}
          changeType="up"
          icon={TrendingUp}
          color="amber"
          loading={loading}
        />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* User growth chart */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white font-semibold" style={{ fontFamily: 'Manrope' }}>User Growth</h3>
            <select
              value={chartPeriod}
              onChange={(e) => setChartPeriod(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userChartData}>
                <defs>
                  <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    background: '#18181b', 
                    border: '1px solid #27272a',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#6366f1" 
                  strokeWidth={2}
                  fill="url(#userGradient)"
                  name="New Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue chart */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white font-semibold" style={{ fontFamily: 'Manrope' }}>Revenue Breakdown</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    background: '#18181b', 
                    border: '1px solid #27272a',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Bar dataKey="platform_share" stackId="a" fill="#6366f1" name="Platform (40%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="creator_share" stackId="a" fill="#8b5cf6" name="Creators (60%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Live streams and top broadcasters */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Live streams */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold" style={{ fontFamily: 'Manrope' }}>Live Now</h3>
              {liveStreams.length > 0 && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
                  {liveStreams.length} active
                </span>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-zinc-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : liveStreams.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {liveStreams.map(stream => (
                <LiveStreamCard key={stream.stream_id} stream={stream} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <Video className="w-12 h-12 mb-3 opacity-50" />
              <p>No live streams at the moment</p>
            </div>
          )}
        </div>

        {/* Top broadcasters */}
        <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
          <h3 className="text-white font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Top Broadcasters</h3>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-14 bg-zinc-800 rounded animate-pulse" />
              ))}
            </div>
          ) : topBroadcasters.length > 0 ? (
            <div className="divide-y divide-zinc-800">
              {topBroadcasters.slice(0, 5).map((broadcaster, index) => (
                <BroadcasterRow 
                  key={broadcaster.email} 
                  broadcaster={broadcaster} 
                  rank={index + 1} 
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <Users className="w-12 h-12 mb-3 opacity-50" />
              <p>No broadcasters yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick stats footer */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-zinc-500 text-sm">Today's Users</p>
          <p className="text-2xl font-bold text-white mt-1">{stats?.today?.newUsers || 0}</p>
        </div>
        <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-zinc-500 text-sm">Today's Streams</p>
          <p className="text-2xl font-bold text-white mt-1">{stats?.today?.newStreams || 0}</p>
        </div>
        <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-zinc-500 text-sm">Weekly Users</p>
          <p className="text-2xl font-bold text-white mt-1">{stats?.thisWeek?.newUsers || 0}</p>
        </div>
        <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
          <p className="text-zinc-500 text-sm">Monthly Revenue</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">{stats?.thisMonth?.revenue?.toFixed(2) || '0.00'}</p>
        </div>
      </div>
    </div>
  );
}
